import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Delete,
  Search as SearchIcon,
  DragIndicator,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import {
  useGetFeaturedProductsQuery,
  useUpdateFeaturedProductsMutation,
  useGetProductsQuery,
} from '../../store/api/adminApi';

interface FeaturedProduct {
  id: string;
  productName: string;
  price: number;
  images: string[];
  status: string;
}

const FeaturedProductsPage: React.FC = () => {
  const { data: featuredData, isLoading } = useGetFeaturedProductsQuery();
  const [updateFeatured] = useUpdateFeaturedProductsMutation();

  const [pickupProducts, setPickupProducts] = useState<FeaturedProduct[]>([]);
  const [firstArtProducts, setFirstArtProducts] = useState<FeaturedProduct[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });
  const [addDialog, setAddDialog] = useState(false);
  const [addTarget, setAddTarget] = useState<'pickup' | 'first_art'>('pickup');
  const [searchInput, setSearchInput] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState<string | undefined>(undefined);
  const [searchPage, setSearchPage] = useState(0);
  const [saving, setSaving] = useState<string | null>(null);

  const [dragType, setDragType] = useState<'pickup' | 'first_art' | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const saveTimerRef = useRef<Record<string, NodeJS.Timeout>>({});
  const initializedRef = useRef(false);

  const { data: searchResults, isLoading: isSearchLoading, isFetching: isSearchFetching } = useGetProductsQuery({
    page: searchPage + 1,
    limit: 10,
    search: submittedSearch ?? '',
  }, {
    skip: !addDialog || submittedSearch === undefined,
  });
  const isSearching = isSearchLoading || isSearchFetching;

  useEffect(() => {
    if (featuredData && !initializedRef.current) {
      setPickupProducts(featuredData.pickup?.products || []);
      setFirstArtProducts(featuredData.firstArt?.products || []);
      initializedRef.current = true;
    }
  }, [featuredData]);

  const autoSave = useCallback(async (type: 'pickup' | 'first_art', products: FeaturedProduct[]) => {
    if (saveTimerRef.current[type]) {
      clearTimeout(saveTimerRef.current[type]);
    }
    saveTimerRef.current[type] = setTimeout(async () => {
      try {
        setSaving(type);
        const productIds = products.map(p => p.id);
        await updateFeatured({ type, productIds }).unwrap();
        setSnackbar({ open: true, message: '自動保存しました', severity: 'success' });
      } catch {
        setSnackbar({ open: true, message: '保存に失敗しました', severity: 'error' });
      } finally {
        setSaving(null);
      }
    }, 800);
  }, [updateFeatured]);

  const handleSearch = () => {
    setSubmittedSearch(searchInput);
    setSearchPage(0);
  };

  const handleAddProduct = (product: any) => {
    const newProduct: FeaturedProduct = {
      id: product.id,
      productName: product.productName || product.title || product.name || '名前なし',
      price: product.price,
      images: product.images || [],
      status: product.status,
    };

    if (addTarget === 'pickup') {
      if (pickupProducts.some(p => p.id === product.id)) return;
      const next = [...pickupProducts, newProduct];
      setPickupProducts(next);
      autoSave('pickup', next);
    } else {
      if (firstArtProducts.some(p => p.id === product.id)) return;
      const next = [...firstArtProducts, newProduct];
      setFirstArtProducts(next);
      autoSave('first_art', next);
    }
  };

  const handleRemoveProduct = (type: 'pickup' | 'first_art', productId: string) => {
    if (type === 'pickup') {
      const next = pickupProducts.filter(p => p.id !== productId);
      setPickupProducts(next);
      autoSave('pickup', next);
    } else {
      const next = firstArtProducts.filter(p => p.id !== productId);
      setFirstArtProducts(next);
      autoSave('first_art', next);
    }
  };

  const moveProduct = (type: 'pickup' | 'first_art', fromIndex: number, toIndex: number) => {
    const setter = type === 'pickup' ? setPickupProducts : setFirstArtProducts;
    const products = type === 'pickup' ? [...pickupProducts] : [...firstArtProducts];
    const [moved] = products.splice(fromIndex, 1);
    products.splice(toIndex, 0, moved);
    setter(products);
    autoSave(type, products);
  };

  const handleDragStart = (type: 'pickup' | 'first_art', index: number) => {
    setDragType(type);
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (type: 'pickup' | 'first_art', toIndex: number) => {
    if (dragType === type && dragIndex !== null && dragIndex !== toIndex) {
      moveProduct(type, dragIndex, toIndex);
    }
    setDragType(null);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragType(null);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const isProductAdded = (productId: string) => {
    if (addTarget === 'pickup') {
      return pickupProducts.some(p => p.id === productId);
    }
    return firstArtProducts.some(p => p.id === productId);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  const renderProductList = (
    title: string,
    type: 'pickup' | 'first_art',
    products: FeaturedProduct[]
  ) => (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight="bold">{title}</Typography>
          <Chip label={`${products.length} 件`} size="small" />
          {saving === type && (
            <Chip
              icon={<CircularProgress size={14} />}
              label="保存中..."
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={() => {
            setAddTarget(type);
            setSearchInput('');
            setSubmittedSearch(undefined);
            setSearchPage(0);
            setAddDialog(true);
          }}
        >
          商品を追加
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {products.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={4}>
          商品が選択されていません。「商品を追加」ボタンから追加してください。
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={1}>
          {products.map((product, index) => (
            <Paper
              key={product.id}
              variant="outlined"
              draggable
              onDragStart={() => handleDragStart(type, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={() => handleDrop(type, index)}
              onDragEnd={handleDragEnd}
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'grab',
                transition: 'all 0.15s',
                borderColor: dragType === type && dragOverIndex === index ? 'primary.main' : undefined,
                borderWidth: dragType === type && dragOverIndex === index ? 2 : 1,
                opacity: dragType === type && dragIndex === index ? 0.4 : 1,
                '&:hover': { bgcolor: 'action.hover' },
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicator sx={{ color: 'text.disabled', fontSize: 20, flexShrink: 0 }} />
              <Chip label={index + 1} size="small" sx={{ minWidth: 32, flexShrink: 0 }} />
              <Avatar
                src={product.images?.[0] || ''}
                variant="rounded"
                sx={{ width: 56, height: 56, flexShrink: 0 }}
              />
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" fontWeight="bold" noWrap>
                  {product.productName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ¥{product.price?.toLocaleString()}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={() => moveProduct(type, index, index - 1)}
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === products.length - 1}
                  onClick={() => moveProduct(type, index, index + 1)}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveProduct(type, product.id)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        特集商品管理
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        ここで選択した商品がユーザーアプリのホーム画面に「アリクイピックアップ作品」「初めてのアートにおすすめ」として表示されます。
        商品の追加・削除・並び替えは自動保存されます。ドラッグまたは矢印ボタンで順序を変更できます。
      </Alert>

      {renderProductList('アリクイピックアップ作品', 'pickup', pickupProducts)}
      {renderProductList('初めてのアートにおすすめ', 'first_art', firstArtProducts)}

      {/* 商品追加ダイアログ */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          商品を追加 - {addTarget === 'pickup' ? 'アリクイピックアップ作品' : '初めてのアートにおすすめ'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            商品名で部分一致検索できます。空欄のまま検索すると全商品を表示します（削除済みを除く）。
          </Alert>
          <Box display="flex" gap={1} mb={2}>
            <TextField
              fullWidth
              placeholder="商品名で検索（空欄で全商品表示）"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.disabled' }} />,
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{ minWidth: 80 }}
            >
              検索
            </Button>
          </Box>
          {submittedSearch === undefined ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                「検索」ボタンを押すか、空欄のまま検索すると全商品を表示します
              </Typography>
            </Box>
          ) : isSearching ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : searchResults?.items ? (
            <>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {searchResults.totalCount || 0} 件の商品が見つかりました
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>画像</TableCell>
                      <TableCell>商品名</TableCell>
                      <TableCell>価格</TableCell>
                      <TableCell>出品者</TableCell>
                      <TableCell>操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary" py={2}>
                            該当する商品がありません
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      searchResults.items.map((product: any) => (
                        <TableRow key={product.id} hover>
                          <TableCell>
                            <Avatar src={product.images?.[0] || ''} variant="rounded" sx={{ width: 48, height: 48 }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {product.productName || product.title || '名前なし'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {product.id?.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>¥{product.price?.toLocaleString()}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {product.sellerName || '不明'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant={isProductAdded(product.id) ? 'outlined' : 'contained'}
                              disabled={isProductAdded(product.id)}
                              onClick={() => handleAddProduct(product)}
                            >
                              {isProductAdded(product.id) ? '追加済み' : '追加'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={searchResults.totalCount || 0}
                page={searchPage}
                onPageChange={(_, newPage) => setSearchPage(newPage)}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
              />
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialog(false); setSubmittedSearch(undefined); }}>閉じる</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeaturedProductsPage;
